export const QUESTION_TYPE_SHORT_ANSWER = "shortAnswer";
export const QUESTION_TYPE_MULTIPLE_CHOICE = "multipleChoice";

export const MIN_MCQ_OPTIONS = 2;
export const MAX_MCQ_OPTIONS = 6;
export const DEFAULT_MCQ_OPTIONS = 4;

let optionIdSequence = 0;

export const getEffectiveQuestionType = (questionType) =>
  questionType === QUESTION_TYPE_MULTIPLE_CHOICE
    ? QUESTION_TYPE_MULTIPLE_CHOICE
    : QUESTION_TYPE_SHORT_ANSWER;

export const getQuestionTypeLabel = (questionType) =>
  getEffectiveQuestionType(questionType) === QUESTION_TYPE_MULTIPLE_CHOICE
    ? "Multiple Choice"
    : "Short Answer";

export const createMcqOptionId = () => {
  optionIdSequence += 1;

  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return `option_${window.crypto.randomUUID()}`;
  }

  return `option_${Date.now().toString(36)}_${optionIdSequence.toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

export const createMcqOption = (text = "") => ({
  id: createMcqOptionId(),
  text,
});

export const createDefaultMcqOptions = (count = DEFAULT_MCQ_OPTIONS) =>
  Array.from({ length: count }, () => createMcqOption());

export const prepareMcqOptionsForEditing = (options) => {
  if (!Array.isArray(options) || options.length === 0) {
    return createDefaultMcqOptions();
  }

  return options.map((option) => {
    if (option && typeof option === "object") {
      return {
        id: option.id ? String(option.id) : createMcqOptionId(),
        text: String(option.text || ""),
      };
    }

    return createMcqOption(String(option || ""));
  });
};

export const addMcqOption = (options) => {
  const currentOptions = Array.isArray(options) ? options : [];
  if (currentOptions.length >= MAX_MCQ_OPTIONS) return currentOptions;
  return [...currentOptions, createMcqOption()];
};

export const updateMcqOptionText = (options, optionId, text) =>
  (Array.isArray(options) ? options : []).map((option) =>
    option.id === optionId ? { ...option, text } : option
  );

export const removeMcqOption = (options, correctOptionId, optionId) => {
  const currentOptions = Array.isArray(options) ? options : [];
  if (currentOptions.length <= MIN_MCQ_OPTIONS) {
    return { options: currentOptions, correctOptionId };
  }

  return {
    options: currentOptions.filter((option) => option.id !== optionId),
    correctOptionId: correctOptionId === optionId ? "" : correctOptionId,
  };
};

const normalizedOptionText = (value) => String(value || "").trim().toLocaleLowerCase();

export const validateMcqQuestion = (draft) => {
  const errors = [];
  const questionText = String(draft?.questionText || "").trim();
  const options = Array.isArray(draft?.options) ? draft.options : [];
  const correctOptionId = String(draft?.correctOptionId || "");

  if (!questionText) {
    errors.push("Enter question text for the multiple-choice question.");
  }

  if (options.length < MIN_MCQ_OPTIONS || options.length > MAX_MCQ_OPTIONS) {
    errors.push(`Use between ${MIN_MCQ_OPTIONS} and ${MAX_MCQ_OPTIONS} answer options.`);
  }

  const optionIds = options.map((option) => String(option?.id || ""));
  if (optionIds.some((id) => !id)) {
    errors.push("Every answer option must have a stable ID.");
  } else if (new Set(optionIds).size !== optionIds.length) {
    errors.push("Answer option IDs must be unique.");
  }

  const optionTexts = options.map((option) => normalizedOptionText(option?.text));
  if (optionTexts.some((text) => !text)) {
    errors.push("Enter text for every answer option.");
  }

  const nonEmptyOptionTexts = optionTexts.filter(Boolean);
  if (new Set(nonEmptyOptionTexts).size !== nonEmptyOptionTexts.length) {
    errors.push("Answer options must not contain duplicate text.");
  }

  if (!correctOptionId) {
    errors.push("Select the correct answer.");
  } else if (optionIds.filter((id) => id === correctOptionId).length !== 1) {
    errors.push("The selected correct answer must match exactly one option.");
  }

  return {
    isValid: errors.length === 0,
    errors,
    value: {
      questionType: QUESTION_TYPE_MULTIPLE_CHOICE,
      questionText,
      options: options.map((option) => ({
        id: String(option.id),
        text: String(option.text).trim(),
      })),
      correctOptionId,
      maxMark: 1,
    },
  };
};

export const buildManualQuestionFields = (draft, questionImageURL = "") => {
  const commonFields = {
    questionText: String(draft?.questionText || ""),
    topic: String(draft?.topic || ""),
    subject: String(draft?.subject || ""),
    level: String(draft?.level || ""),
    specCode: String(draft?.specCode || ""),
  };

  if (questionImageURL) commonFields.questionImageURL = questionImageURL;

  if (getEffectiveQuestionType(draft?.questionType) === QUESTION_TYPE_MULTIPLE_CHOICE) {
    const validation = validateMcqQuestion(draft);
    if (!validation.isValid) {
      const error = new Error(validation.errors.join(" "));
      error.validationErrors = validation.errors;
      throw error;
    }

    return { ...commonFields, ...validation.value };
  }

  return {
    ...commonFields,
    markSchemeAnswer: String(draft?.markSchemeAnswer || ""),
    maxMark: parseInt(draft?.maxMark, 10),
    textBoxSize: draft?.textBoxSize || "medium",
  };
};
